import React, { useEffect, useState, useRef } from "react";
import { Switch, Route, useHistory, useLocation } from "react-router-dom";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Sidenav from "common/Sidenav";
import theme from "assets/theme";
import routes, { RouteAccessType, hiddenRoutes } from "routes";
import { useAuth } from "auth-context/auth.context";
import DotSpinner from "common/Loader/Loader";
import PropTypes from "prop-types";

// Componente CustomRoute para reemplazar ProtectedRoute
const CustomRoute = ({ component: Component, access = RouteAccessType.PUBLIC, ...rest }) => {
  const { user, permissions, isSignedIn, hasRole } = useAuth();
  const history = useHistory();
  const isUserAdmin = hasRole("ADMIN");
  const currentPath = rest.path;
  const routeProcessedRef = useRef(false);

  // Este efecto maneja redirecciones solo una vez por montaje de componente
  useEffect(() => {
    // Si ya procesamos esta ruta, no hacer nada
    if (routeProcessedRef.current) return;
    routeProcessedRef.current = true;

    // Esperar un poco para asegurarnos que todo esté sincronizado
    const timeoutId = setTimeout(() => {
      // Si la ruta es la página de login y el usuario está autenticado
      if (currentPath === "/authentication/sign-in" && isSignedIn && user) {
        console.log("Redirigiendo a bienvenida desde CustomRoute (usuario autenticado en login)");
        history.replace("/bienvenida");
        return;
      }

      // Ruta privada pero usuario no autenticado
      if (access !== RouteAccessType.PUBLIC && (!isSignedIn || !user)) {
        if (currentPath !== "/authentication/sign-in") {
          console.log("Redirigiendo a login desde CustomRoute (usuario no autenticado)");
          history.replace("/authentication/sign-in");
          return;
        }
      }

      // Ruta privada, usuario autenticado pero sin permisos
      if (access !== RouteAccessType.PUBLIC && isSignedIn && user) {
        const hasPermission =
          isUserAdmin ||
          access === RouteAccessType.ALL_AUTH ||
          (access === RouteAccessType.PROTECTED &&
            permissions?.some((perm) => perm.route === currentPath && perm.has_access));

        if (!hasPermission && currentPath !== "/sin-autorizacion") {
          console.log("Redirigiendo a sin-autorizacion desde CustomRoute (sin permiso)");
          history.replace("/sin-autorizacion");
          return;
        }
      }
    }, 100); // Pequeño retraso para asegurar que el estado esté actualizado

    return () => {
      clearTimeout(timeoutId);
      routeProcessedRef.current = false;
    };
  }, [currentPath, isSignedIn, user, history, access, permissions, isUserAdmin]);

  const hasPermission = () => {
    if (isUserAdmin) return true;
    if (access === RouteAccessType.ALL_AUTH) return true;
    if (access === RouteAccessType.ADMIN) return isUserAdmin;
    if (access === RouteAccessType.PROTECTED) {
      return permissions?.some((perm) => perm.route === currentPath && perm.has_access);
    }
    return false;
  };

  // Ruta pública, renderizar siempre
  if (access === RouteAccessType.PUBLIC) {
    return <Route {...rest} render={(props) => <Component {...props} />} />;
  }

  // Login page con usuario autenticado, no renderizar nada (redirección manejada por useEffect)
  if (currentPath === "/authentication/sign-in" && isSignedIn && user) {
    return null;
  }

  // Ruta privada pero usuario no autenticado, no renderizar (redirección manejada por useEffect)
  if (access !== RouteAccessType.PUBLIC && (!isSignedIn || !user)) {
    return null;
  }

  // Ruta privada, usuario autenticado pero sin permisos, no renderizar (redirección manejada por useEffect)
  if (access !== RouteAccessType.PUBLIC && isSignedIn && user && !hasPermission()) {
    return null;
  }

  // Si llegamos aquí, el usuario tiene permisos para ver la ruta
  return <Route {...rest} render={(props) => <Component {...props} />} />;
};

CustomRoute.propTypes = {
  component: PropTypes.elementType.isRequired,
  access: PropTypes.oneOf(Object.values(RouteAccessType)),
  path: PropTypes.string,
  location: PropTypes.object,
};

// Página no encontrada que redirige según autenticación
const NotFoundPage = () => {
  const { isSignedIn, user } = useAuth();
  const history = useHistory();
  const { pathname } = useLocation();
  const redirectDoneRef = useRef(false);

  useEffect(() => {
    if (redirectDoneRef.current) return;
    redirectDoneRef.current = true;

    // Pequeño retraso para asegurar que el estado está actualizado
    const timeoutId = setTimeout(() => {
      const targetPath = isSignedIn && user ? "/bienvenida" : "/authentication/sign-in";

      // No redirigir si ya estamos en la ruta destino
      if (pathname !== targetPath) {
        console.log(`Redirigiendo de ${pathname} a ${targetPath} (ruta no encontrada)`);
        history.replace(targetPath);
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      redirectDoneRef.current = false;
    };
  }, [pathname, history, isSignedIn, user]);

  return null;
};

export default function App() {
  const { pathname } = useLocation();
  const history = useHistory();
  const { user, isSignedIn, isLoading } = useAuth();
  const [routeComponents, setRouteComponents] = useState([]);
  const initialCheckDoneRef = useRef(false);

  // Gestionar desplazamiento al cambiar de ruta
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Verificación inicial de autenticación al cargar la aplicación
  useEffect(() => {
    // Evitar múltiples verificaciones
    if (initialCheckDoneRef.current || isLoading) return;

    // Pequeño retraso para asegurar que todo está sincronizado
    const timeoutId = setTimeout(() => {
      if (isSignedIn && user) {
        if (pathname === "/authentication/sign-in" || pathname === "/") {
          console.log("Redirigiendo a bienvenida inicialmente (usuario autenticado)");
          history.replace("/bienvenida");
        }
      } else if (
        pathname !== "/authentication/sign-in" &&
        !pathname.startsWith("/authentication/")
      ) {
        console.log("Redirigiendo a login inicialmente (usuario no autenticado)");
        history.replace("/authentication/sign-in");
      }

      initialCheckDoneRef.current = true;
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [isSignedIn, user, pathname, history, isLoading]);

  // Inicialización de rutas
  useEffect(() => {
    if (isLoading) return;

    const buildRoutes = () => {
      const processedRoutes = [];

      const processRoute = (route, parentKey = "") => {
        if (route.type === "nested" && route.children) {
          route.children.forEach((child) => {
            processedRoutes.push(
              <CustomRoute
                path={child.route}
                component={child.component}
                key={`${parentKey}${child.key}`}
                access={child.access}
              />
            );
          });
        } else if (route.route && route.component) {
          processedRoutes.push(
            <CustomRoute
              path={route.route}
              component={route.component}
              key={`${parentKey}${route.key}`}
              access={route.access}
            />
          );
        }
      };

      // Procesar rutas principales
      routes.forEach((route) => {
        if (route.collapse) {
          route.collapse.forEach((collapseRoute) => processRoute(collapseRoute));
        } else {
          processRoute(route);
        }
      });

      // Procesar rutas ocultas
      hiddenRoutes.forEach((hiddenRoute) => {
        processedRoutes.push(
          <CustomRoute
            path={hiddenRoute.route}
            component={hiddenRoute.component}
            key={`hidden_${hiddenRoute.key}`}
            access={hiddenRoute.access}
          />
        );
      });

      return processedRoutes;
    };

    setRouteComponents(buildRoutes());
  }, [isLoading]);

  // Mostrar loader mientras carga
  if (isLoading) {
    return <DotSpinner />;
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {isSignedIn && user ? (
          <>
            <Sidenav routes={routes} />
            <Switch>
              {routeComponents}
              <Route path="*" component={NotFoundPage} />
            </Switch>
          </>
        ) : (
          <Switch>
            {routeComponents}
            <Route path="*" component={NotFoundPage} />
          </Switch>
        )}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
